import React, { useState, useEffect } from 'react';
import { Box, Divider, Typography, useMediaQuery, useTheme, Button } from '@mui/material';
import { gql, useQuery,useLazyQuery } from '@apollo/client';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import CostOverview from './components/CostOverview';
import SpendAnalysisChart from './components/SpendAnalysis';
import CostDistributionChart from './components/CostDistributionChart';
import CostCenter from './components/CostCenter';
import CostByRegion from './components/CostByRegion';
import QuickActions from './components/QuickActions';
import RecentActivity from './components/RecentActivity';
import { PageName } from './types';
import type { Tenant, TenantMembership } from './App';


import dayjs from 'dayjs'; 


// Constants
const SIDEBAR_WIDTH_OPEN = 212;
const SIDEBAR_WIDTH_CLOSED = 70;
const INFO_PANEL_WIDTH_LG = '300px';
const HEADER_HEIGHT = '68px';

// --- Main Dashboard Overview Data Query ---
const GET_DASHBOARD_OVERVIEW_DATA = gql`
  query GetDashboardOverviewData($tenantId: ID!) {
    overview: getOverview(tenantId: $tenantId) {
      kpis {
        totalSpend
        forecastedSpend
        totalKubernetesCost
        creditsUsed
        totalSpendChangePercentage
        forecastedSpendChangePercentage
        totalKubernetesCostChangePercentage
      }
      providerBreakdown {
        provider
        cost
      }
      spendAnalysis {
        date
        spend
        forecast
      }
      costCenterBreakdown {
        date
        gcp
        aws
        azure
        langfuse
      }
      costByRegion {
        cost
        region
      }
    }
  }
`;

// --- NEW: Query to get Data Source Sync Status (with isSyncing field) ---
const GET_DATA_SOURCE_SYNC_STATUS = gql`
  query DataSourcesByTenant($tenantId: ID!) {
    dataSourcesByTenant(tenantId: $tenantId) {
      isSyncing
      lastSyncedAt
    }
  }
`;

// --- Component Interfaces ---
interface DashboardProps {
  isBlurred: boolean;
  onLogout: () => Promise<void>;
  onAddNewTenant: () => void;
  currentTenant: Tenant | null;
  allTenants: Tenant[];
  isLoadingTenants: boolean;
  onChangeTenant: (tenantId: string) => void;
  onTenantDeleted: (tenantId: string) => void;
  activePage: PageName;
  userRole?: string | null;
  tenantMemberships?: TenantMembership[] | null;
  userName?: string | null;
  userEmail?: string | null;
  userProfilePicture?: string | null;
}


// --- Custom Spinner Component ---
const CustomSpinner: React.FC = () => (
  <Box sx={{ width: 70, height: 70, mb: 3 }}>
    <style>
      {`
        @keyframes spin {
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
    <svg 
      width="100%" 
      height="100%" 
      viewBox="0 0 38 38" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ animation: 'spin 2.5s linear infinite' }}
    >
      <g fill="#0052FF" fillRule="evenodd">
        {/* Each spoke is positioned at the top and then rotated around the center (19, 19) */}
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="1"    transform="rotate(0, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.91" transform="rotate(30, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.83" transform="rotate(60, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.75" transform="rotate(90, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.66" transform="rotate(120, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.58" transform="rotate(150, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.5"  transform="rotate(180, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.41" transform="rotate(210, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.33" transform="rotate(240, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.25" transform="rotate(270, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.16" transform="rotate(300, 19, 19)"/>
        <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.08" transform="rotate(330, 19, 19)"/>
      </g>
    </svg>
  </Box>
);

// --- UI Components ---
const InitialFetchOverlay: React.FC = () => (
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(5px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 10, p: 2, textAlign: 'center' }}>
        <CustomSpinner />
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>We're Preparing Your New Month's Data</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '420px' }}>It's the first day of the month, and we're busy crunching the latest numbers for your dashboard. Please check back shortly.</Typography>
    </Box>
);


interface EmptyStateOverlayProps {
  onNavigate?: () => void;
  userRole?: string | null;
  title: string;
  subtitle: string;
  imageSrc: string;
}


const largeScaleData = Array.from({ length: 30 }, (_, i) => ({
  date: `2023-11-${i + 1}`,
  spend: 1_500_000 + Math.random() * 500_000, // Values between 1.5M and 2.0M
  forecast: 1_500_000 + Math.random() * 500_000,
}));

const EmptyStateOverlay: React.FC<EmptyStateOverlayProps> = ({ onNavigate, userRole, title, subtitle, imageSrc }) => (
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(5px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 10, p: 2, textAlign: 'center', marginRight:"30px", marginBottom:"30px" }}>
      <img src={imageSrc} alt="No Data" style={{ width: '150px', height: '150px', marginBottom: '2px' }} />
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1,fontFamily:"inter"  }}>{title}</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1, maxWidth: '420px',fontFamily:"inter" }}>{subtitle}</Typography>
      
      {onNavigate && userRole !== 'viewer' && (
          <Button disableElevation variant="contained" onClick={onNavigate} sx={{fontFamily:'Inter', backgroundColor:'#0064FF', fontSize: '14px', fontWeight: 500, borderRadius: '8px', textTransform: 'none' }}>
              Add New Data Source
          </Button>
      )} 
    </Box>
);

const DataSyncingOverlay: React.FC = () => (
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(5px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 10, p: 2, textAlign: 'center' }}>
        <CustomSpinner />
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Syncing Your Dashboard Data</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '420px' }}>Preparing your dashboard. We're syncing your data sources and will have everything ready for you in a moment.</Typography>
    </Box>
);

// --- Main Dashboard Component ---
const DashboardReactComponent: React.FC<DashboardProps> = ({
  isBlurred,
  onLogout,
  onAddNewTenant,
  currentTenant,
  allTenants,
  isLoadingTenants,
  onChangeTenant,
  onTenantDeleted,
  activePage,
  userRole,
  tenantMemberships,
  userName,
  userEmail,
  userProfilePicture,
}) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const isFirstDayOfMonth = new Date().getDate() === 1;


  const hasDataSources = !!currentTenant?.id && !!currentTenant.dataSourceCount && currentTenant.dataSourceCount > 0;

  // --- Query for Data Source Sync Status (with isSyncing field) ---
  const {
    data: dataSourceSyncStatusData,
    loading: loadingDataSourceSyncStatus,
  } = useQuery(GET_DATA_SOURCE_SYNC_STATUS, {
    variables: { tenantId: currentTenant?.id },
    skip: !currentTenant?.id || !hasDataSources,
    pollInterval: (currentTenant?.id && currentTenant.dataSourceCount === 1) ? 5000 : 0,
  });


  const isSingleDataSourceCurrentlySyncing =
  currentTenant?.dataSourceCount === 1 &&
  dataSourceSyncStatusData?.dataSourcesByTenant?.length === 1 &&
  dataSourceSyncStatusData?.dataSourcesByTenant?.[0]?.isSyncing === true;

  // --- Main Dashboard Overview Data Query ---
  const { data, loading, refetch } = useQuery(GET_DASHBOARD_OVERVIEW_DATA, {
    variables: {
      tenantId: currentTenant?.id,
    },
    skip: !hasDataSources || isSingleDataSourceCurrentlySyncing,
    pollInterval: isSingleDataSourceCurrentlySyncing ? 5000 : 0,
  });
  // console.log("data--->", data);
const isDataEffectivelyEmpty =
  !loading &&
  data?.overview?.kpis?.totalSpend === 0 &&
  data?.overview?.spendAnalysis?.length === 0;


  // Effect to manage polling for the main dashboard data
  useEffect(() => {
    if (isSingleDataSourceCurrentlySyncing) {
      refetch({ pollInterval: 5000 });
    } else {
      refetch({ pollInterval: 0 });
    }
  }, [isSingleDataSourceCurrentlySyncing, refetch]);


  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const handleNavigate = (page: PageName) => navigate(`/${page}`);
  const navigateToAddDataSource = () => {
    if (currentTenant) {
      navigate('/data-sources/add', { state: { tenantId: currentTenant.id } });
    }
  };

  const currentSidebarWidth = sidebarOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED;

  const showNoTenantMessage = !currentTenant;
  const showNoDataSourcesMessage = !!currentTenant && (currentTenant.dataSourceCount !== undefined && currentTenant.dataSourceCount === 0);

const showInitialFetchMessage =
  isFirstDayOfMonth &&
  (loading || isDataEffectivelyEmpty) && 
  !showNoTenantMessage &&
  !showNoDataSourcesMessage &&
  !isSingleDataSourceCurrentlySyncing;
  const showSyncingOverlay = isSingleDataSourceCurrentlySyncing;

  const isContentLoading = loading || showNoDataSourcesMessage || showInitialFetchMessage || showSyncingOverlay;


  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default', filter: isBlurred ? 'blur(8px)' : 'none', transition: 'filter 0.3s ease-in-out' }}>
      <Sidebar
        key={activePage}
        open={sidebarOpen}
        drawerWidth={currentSidebarWidth}
        toggleSidebar={toggleSidebar}
        onNavigate={handleNavigate}
        activePage={activePage}
        sx={{ zIndex: 1201, flexShrink: 0 }}
        onAddNewTenant={onAddNewTenant}
        currentTenant={currentTenant}
        allTenants={allTenants}
        isLoadingTenants={isLoadingTenants || loadingDataSourceSyncStatus}
        onChangeTenant={onChangeTenant}
        onTenantDeleted={onTenantDeleted}
        userRole={userRole}
        tenantMemberships={tenantMemberships}
      />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', width: `calc(100% - ${currentSidebarWidth}px)`, transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), }}>
        <Header key={activePage} toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} activePage={activePage} sx={{ zIndex: 1100, flexShrink: 0 }} userName={userName} onLogout={onLogout} userEmail={userEmail} userProfilePicture={userProfilePicture} />
        <Box sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
          
          {activePage === 'dashboard' && showSyncingOverlay && <DataSyncingOverlay />}
          {activePage === 'dashboard' && showInitialFetchMessage && <InitialFetchOverlay />}
          
          {activePage === 'dashboard' && showNoTenantMessage && (
            <EmptyStateOverlay
              title="Welcome to Your Dashboard"
              subtitle="Please add a tenant from the sidebar to get started."
              imageSrc="/icons/no-dashboard-data.svg"
              userRole={userRole}
            />
          )}

          {activePage === 'dashboard' && showNoDataSourcesMessage && (
          <EmptyStateOverlay
            title="Let's Set Up Your Dashboard"
            subtitle="Populate the dashboard by adding your first data source for the selected tenant."
            imageSrc="/icons/No-data-src-added-log.svg"
            onNavigate={navigateToAddDataSource}
            userRole={userRole}
          />
        )}

          <Box component="main" sx={{ position: 'relative', flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
            {activePage === 'dashboard' ? (
              <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <CostOverview userName={userName} kpiData={data?.overview?.kpis} isLoading={isContentLoading} />
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                  <Box sx={{ width: '100%', flexGrow: 1 }}>                 
                    <SpendAnalysisChart data={data?.overview?.spendAnalysis || []} />
                  </Box>
                  <Box sx={{ width: '100%', flexBasis: { md: '40%' }, flexGrow: { md: 0 } }}>
                    <CostDistributionChart providerData={data?.overview?.providerBreakdown} isLoading={isContentLoading} />
                  </Box>
                </Box>

                <CostCenter data={data?.overview?.costCenterBreakdown || []} />
                <Box sx={{ width: '100%' }}>
                  <CostByRegion costByRegionData={data?.overview?.costByRegion} isLoading={isContentLoading} />
                </Box>
              </Box>
            ) : (
              <Outlet context={{
                  userRole,
                  userName,
                  userEmail,
                  currentTenant,
                  userProfilePicture,
              }} />
            )}
          </Box>
          {activePage === 'dashboard' && isLargeScreen && (
            <Box sx={{ width: { lg: INFO_PANEL_WIDTH_LG }, flexShrink: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', height: `calc(100vh - ${HEADER_HEIGHT})`, overflowY: 'auto', overflowX: 'hidden', borderLeft: `0.7px solid ${theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800]}`, }}>
              <QuickActions currentTenant={currentTenant} />
              <Divider sx={{ my: theme.spacing(2.5), mx: -2.5}} />
              <RecentActivity currentTenantId={currentTenant?.id || null} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
export default DashboardReactComponent;


// import React, { useState, useEffect } from 'react';
// import { Box, Divider, Typography, useMediaQuery, useTheme, Button } from '@mui/material';
// import { gql, useQuery,useLazyQuery } from '@apollo/client';
// import { Outlet, useNavigate } from 'react-router-dom';
// import Sidebar from './components/Sidebar';
// import Header from './components/Header';
// import CostOverview from './components/CostOverview';
// import SpendAnalysisChart from './components/SpendAnalysis';
// import CostDistributionChart from './components/CostDistributionChart';
// import CostCenter from './components/CostCenter';
// import CostByRegion from './components/CostByRegion';
// import QuickActions from './components/QuickActions';
// import RecentActivity from './components/RecentActivity';
// import { PageName } from './types';
// import type { Tenant, TenantMembership } from './App';


// import dayjs from 'dayjs'; 


// // Constants
// const SIDEBAR_WIDTH_OPEN = 212;
// const SIDEBAR_WIDTH_CLOSED = 70;
// const INFO_PANEL_WIDTH_LG = '300px';
// const HEADER_HEIGHT = '68px';

// // --- Main Dashboard Overview Data Query ---
// const GET_DASHBOARD_OVERVIEW_DATA = gql`
//   query GetDashboardOverviewData($tenantId: ID!) {
//     overview: getOverview(tenantId: $tenantId) {
//       kpis {
//         totalSpend
//         forecastedSpend
//         totalKubernetesCost
//         creditsUsed
//         totalSpendChangePercentage
//         forecastedSpendChangePercentage
//         totalKubernetesCostChangePercentage
//       }
//       providerBreakdown {
//         provider
//         cost
//       }
//       spendAnalysis {
//         date
//         spend
//         forecast
//       }
//       costCenterBreakdown {
//         date
//         gcp
//         aws
//         azure
//       }
//       costByRegion {
//         cost
//         region
//       }
//     }
//   }
// `;

// // --- NEW: Query to get Data Source Sync Status (with isSyncing field) ---
// const GET_DATA_SOURCE_SYNC_STATUS = gql`
//   query DataSourcesByTenant($tenantId: ID!) {
//     dataSourcesByTenant(tenantId: $tenantId) {
//       isSyncing
//       lastSyncedAt
//     }
//   }
// `;

// // --- Component Interfaces ---
// interface DashboardProps {
//   isBlurred: boolean;
//   onLogout: () => Promise<void>;
//   onAddNewTenant: () => void;
//   currentTenant: Tenant | null;
//   allTenants: Tenant[];
//   isLoadingTenants: boolean;
//   onChangeTenant: (tenantId: string) => void;
//   onTenantDeleted: (tenantId: string) => void;
//   activePage: PageName;
//   userRole?: string | null;
//   tenantMemberships?: TenantMembership[] | null;
//   userName?: string | null;
//   userEmail?: string | null;
//   userProfilePicture?: string | null;
// }


// // --- Custom Spinner Component ---
// // --- Custom Spinner Component ---
// const CustomSpinner: React.FC = () => (
//   <Box sx={{ width: 70, height: 70, mb: 3 }}>
//     <style>
//       {`
//         @keyframes spin {
//           100% { transform: rotate(360deg); }
//         }
//       `}
//     </style>
//     <svg 
//       width="100%" 
//       height="100%" 
//       viewBox="0 0 38 38" 
//       xmlns="http://www.w3.org/2000/svg"
//       style={{ animation: 'spin 2.5s linear infinite' }}
//     >
//       <g fill="#0052FF" fillRule="evenodd">
//         {/* Each spoke is positioned at the top and then rotated around the center (19, 19) */}
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="1"    transform="rotate(0, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.91" transform="rotate(30, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.83" transform="rotate(60, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.75" transform="rotate(90, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.66" transform="rotate(120, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.58" transform="rotate(150, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.5"  transform="rotate(180, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.41" transform="rotate(210, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.33" transform="rotate(240, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.25" transform="rotate(270, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.16" transform="rotate(300, 19, 19)"/>
//         <rect x="17" y="0" width="4" height="10" rx="2" opacity="0.08" transform="rotate(330, 19, 19)"/>
//       </g>
//     </svg>
//   </Box>
// );

// // --- UI Components ---
// const InitialFetchOverlay: React.FC = () => (
//     <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(5px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 10, p: 2, textAlign: 'center' }}>
//       {/* <CircularProgress sx={{ mb: 3 }} /> */}
//         <CustomSpinner />
//       <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>We're Preparing Your New Month's Data</Typography>
//       <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '420px' }}>It's the first day of the month, and we're busy crunching the latest numbers for your dashboard. Please check back shortly.</Typography>
//     </Box>
// );


// interface EmptyStateOverlayProps {
//   onNavigate?: () => void;
//   userRole?: string | null;
//   title: string;
//   subtitle: string;
//   imageSrc: string;
// }


// const largeScaleData = Array.from({ length: 30 }, (_, i) => ({
//   date: `2023-11-${i + 1}`,
//   spend: 1_500_000 + Math.random() * 500_000, // Values between 1.5M and 2.0M
//   forecast: 1_500_000 + Math.random() * 500_000,
// }));

// const EmptyStateOverlay: React.FC<EmptyStateOverlayProps> = ({ onNavigate, userRole, title, subtitle, imageSrc }) => (
//     <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(5px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 10, p: 2, textAlign: 'center', marginRight:"30px", marginBottom:"30px" }}>
//       <img src={imageSrc} alt="No Data" style={{ width: '150px', height: '150px', marginBottom: '2px' }} />
//       <Typography variant="h6" sx={{ fontWeight: 600, mb: 1,fontFamily:"inter"  }}>{title}</Typography>
//       <Typography variant="body1" color="text.secondary" sx={{ mb: 1, maxWidth: '420px',fontFamily:"inter" }}>{subtitle}</Typography>
      
//       {/* This logic ensures the button ONLY shows when onNavigate is passed */}
//       {onNavigate && userRole !== 'viewer' && (
//           <Button disableElevation variant="contained" onClick={onNavigate} sx={{fontFamily:'Inter', backgroundColor:'#0064FF', fontSize: '14px', fontWeight: 500, borderRadius: '8px', textTransform: 'none' }}>
//               Add New Data Source
              
//           </Button>
//       )} 
//     </Box>
// );

// // --- NEW: Data Syncing Overlay Component with simplified text ---
// const DataSyncingOverlay: React.FC = () => (
//     <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(5px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 10, p: 2, textAlign: 'center' }}>
//       {/* <CircularProgress sx={{ mb: 3 }} /> */}
//               <CustomSpinner />

//       <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Syncing Your Dashboard Data</Typography>
//       <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '420px' }}>Preparing your dashboard. We're syncing your data sources and will have everything ready for you in a moment.</Typography>
//     </Box>
// );

// // --- Main Dashboard Component ---
// const DashboardReactComponent: React.FC<DashboardProps> = ({
//   isBlurred,
//   onLogout,
//   onAddNewTenant,
//   currentTenant,
//   allTenants,
//   isLoadingTenants,
//   onChangeTenant,
//   onTenantDeleted,
//   activePage,
//   userRole,
//   tenantMemberships,
//   userName,
//   userEmail,
//   userProfilePicture,
// }) => {
//   const theme = useTheme();
//   const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
//   const navigate = useNavigate();
//   const [sidebarOpen, setSidebarOpen] = useState(true);
//   const isFirstDayOfMonth = new Date().getDate() === 1;


//   const hasDataSources = !!currentTenant?.id && !!currentTenant.dataSourceCount && currentTenant.dataSourceCount > 0;

//   // --- Query for Data Source Sync Status (with isSyncing field) ---
//   const {
//     data: dataSourceSyncStatusData,
//     loading: loadingDataSourceSyncStatus,
//   } = useQuery(GET_DATA_SOURCE_SYNC_STATUS, {
//     variables: { tenantId: currentTenant?.id },
//     skip: !currentTenant?.id || !hasDataSources,
//     pollInterval: (currentTenant?.id && currentTenant.dataSourceCount === 1) ? 5000 : 0,
//   });

//     // const isSingleDataSourceCurrentlySyncing =true;

//   const isSingleDataSourceCurrentlySyncing =
//   currentTenant?.dataSourceCount === 1 &&
//   dataSourceSyncStatusData?.dataSourcesByTenant?.length === 1 &&
//   dataSourceSyncStatusData?.dataSourcesByTenant?.[0]?.isSyncing === true;

//   // --- Main Dashboard Overview Data Query ---
//   const { data, loading, refetch } = useQuery(GET_DASHBOARD_OVERVIEW_DATA, {
//     variables: {
//       tenantId: currentTenant?.id,
//     },
//     skip: !hasDataSources || isSingleDataSourceCurrentlySyncing,
//     pollInterval: isSingleDataSourceCurrentlySyncing ? 5000 : 0,
//   });
//   console.log("data--->", data);
//   // NEW: Check if the fetched data is empty/unprocessed
// const isDataEffectivelyEmpty =
//   !loading && // Make sure the query has finished
//   data?.overview?.kpis?.totalSpend === 0 &&
//   data?.overview?.spendAnalysis?.length === 0;


//   // Effect to manage polling for the main dashboard data
//   useEffect(() => {
//     if (isSingleDataSourceCurrentlySyncing) {
//       refetch({ pollInterval: 5000 });
//     } else {
//       refetch({ pollInterval: 0 });
//     }
//   }, [isSingleDataSourceCurrentlySyncing, refetch]);


//   const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
//   const handleNavigate = (page: PageName) => navigate(`/${page}`);
//   const navigateToAddDataSource = () => {
//     if (currentTenant) {
//       navigate('/data-sources/add', { state: { tenantId: currentTenant.id } });
//     }
//   };

//   const currentSidebarWidth = sidebarOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED;

//   // --- [MODIFIED] Overlay and Content Display Logic for two scenarios ---
//   const showNoTenantMessage = !currentTenant;
//   const showNoDataSourcesMessage = !!currentTenant && (currentTenant.dataSourceCount !== undefined && currentTenant.dataSourceCount === 0);

//   // const showInitialFetchMessage = isFirstDayOfMonth && !data?.overview && !showNoTenantMessage && !showNoDataSourcesMessage && !isSingleDataSourceCurrentlySyncing;

//   // NEW, more robust logic
// const showInitialFetchMessage =
//   isFirstDayOfMonth &&
//   (loading || isDataEffectivelyEmpty) && // This is the key change
//   !showNoTenantMessage &&
//   !showNoDataSourcesMessage &&
//   !isSingleDataSourceCurrentlySyncing;
//   const showSyncingOverlay = isSingleDataSourceCurrentlySyncing;

//   // Show skeletons only when a tenant is selected but is waiting for data.
//   const isContentLoading = loading || showNoDataSourcesMessage || showInitialFetchMessage || showSyncingOverlay;


//   return (
//     <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default', filter: isBlurred ? 'blur(8px)' : 'none', transition: 'filter 0.3s ease-in-out' }}>
//       <Sidebar
//         key={activePage}
//         open={sidebarOpen}
//         drawerWidth={currentSidebarWidth}
//         toggleSidebar={toggleSidebar}
//         onNavigate={handleNavigate}
//         activePage={activePage}
//         sx={{ zIndex: 1201, flexShrink: 0 }}
//         onAddNewTenant={onAddNewTenant}
//         currentTenant={currentTenant}
//         allTenants={allTenants}
//         isLoadingTenants={isLoadingTenants || loadingDataSourceSyncStatus}
//         onChangeTenant={onChangeTenant}
//         onTenantDeleted={onTenantDeleted}
//         userRole={userRole}
//         tenantMemberships={tenantMemberships}
//       />
//       <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', width: `calc(100% - ${currentSidebarWidth}px)`, transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), }}>
//         <Header key={activePage} toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} activePage={activePage} sx={{ zIndex: 1100, flexShrink: 0 }} userName={userName} onLogout={onLogout} userEmail={userEmail} userProfilePicture={userProfilePicture} />
//         <Box sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
          
//           {/* --- [MODIFIED] Conditional rendering for overlays --- */}
//           {activePage === 'dashboard' && showSyncingOverlay && <DataSyncingOverlay />}
//           {activePage === 'dashboard' && showInitialFetchMessage && <InitialFetchOverlay />}
          
//           {/* Scenario 1: No Tenant Selected */}
//           {activePage === 'dashboard' && showNoTenantMessage && (
//             <EmptyStateOverlay
//               title="Welcome to Your Dashboard"
//               subtitle="Please add a tenant from the sidebar to get started."
//               imageSrc="/icons/no-dashboard-data.svg"
//               userRole={userRole}
//             />
//           )}

//           {/* Scenario 2: Tenant Selected, No Data Sources */}
//           {activePage === 'dashboard' && showNoDataSourcesMessage && (
//           <EmptyStateOverlay
//             title="Let's Set Up Your Dashboard"
//             subtitle="Populate the dashboard by adding your first data source for the selected tenant."
//             imageSrc="/icons/No-data-src-added-log.svg"
//             onNavigate={navigateToAddDataSource}
//             userRole={userRole}
//           />
//         )}

//           <Box component="main" sx={{ position: 'relative', flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
//             {activePage === 'dashboard' ? (
//               <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: '16px' }}>
//                 <CostOverview userName={userName} kpiData={data?.overview?.kpis} isLoading={isContentLoading} />
//                 <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
//                   <Box sx={{ width: '100%', flexGrow: 1 }}> 
//                           {/* <SpendAnalysisChart data={largeScaleData} />                  */}
//                     <SpendAnalysisChart data={data?.overview?.spendAnalysis || []} />
//                   </Box>
//                   <Box sx={{ width: '100%', flexBasis: { md: '40%' }, flexGrow: { md: 0 } }}>
//                     <CostDistributionChart providerData={data?.overview?.providerBreakdown} isLoading={isContentLoading} />
//                   </Box>
//                 </Box>
//                 {/* <CostCenter data={typicalNumberData} /> */}

//                 <CostCenter data={data?.overview?.costCenterBreakdown || []} />
//                 {/* <CostByRegion costByRegionData={data?.overview?.costByRegion} isLoading={isContentLoading} /> */}
//                 <Box sx={{ width: '100%' }}>
//                   <CostByRegion costByRegionData={data?.overview?.costByRegion} isLoading={isContentLoading} />
//                 </Box>
//               </Box>
//             ) : (
//               <Outlet context={{
//                   userRole,
//                   userName,
//                   userEmail,
//                   currentTenant,
//                   userProfilePicture,
//               }} />
//             )}
//           </Box>
//           {activePage === 'dashboard' && isLargeScreen && (
//             <Box sx={{ width: { lg: INFO_PANEL_WIDTH_LG }, flexShrink: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', height: `calc(100vh - ${HEADER_HEIGHT})`, overflowY: 'auto', overflowX: 'hidden', borderLeft: `0.7px solid ${theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800]}`, }}>
//               <QuickActions currentTenant={currentTenant} />
//               <Divider sx={{ my: theme.spacing(2.5), mx: -2.5}} />
//               <RecentActivity currentTenantId={currentTenant?.id || null} />
//             </Box>
//           )}
//         </Box>
//       </Box>
//     </Box>
//   );
// };
// export default DashboardReactComponent;
